/**
 * Story 052 — Security section: change password + sessions + MFA.
 */
import { useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { SectionShell } from "./SectionShell.js";
import { ChangePasswordDialog } from "./ChangePasswordDialog.js";
import {
  useEnrollMfa,
  useMfaFactors,
  useSessions,
  useTerminateOtherSessions,
  useTerminateSession,
} from "../queries/index.js";
import { useToast } from "../ToastContext.js";
import { KeyRoundIcon } from "@aeriosiq/ui/icons";

export function SecuritySection(): ReactElement {
  const [pwdOpen, setPwdOpen] = useState(false);
  return (
    <SectionShell id="security" title="Security">
      <Stack spacing={4}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Password
          </Typography>
          <Button
            variant="outlined"
            startIcon={<KeyRoundIcon />}
            onClick={() => {
              setPwdOpen(true);
            }}
          >
            Change password
          </Button>
          <ChangePasswordDialog
            open={pwdOpen}
            onClose={() => {
              setPwdOpen(false);
            }}
          />
        </Box>
        <SessionsTable />
        <MfaTable />
      </Stack>
    </SectionShell>
  );
}

function SessionsTable(): ReactElement {
  const { data } = useSessions();
  const terminate = useTerminateSession();
  const terminateOthers = useTerminateOtherSessions();
  const { toast } = useToast();
  const [confirmAll, setConfirmAll] = useState(false);

  const items = data?.items ?? [];
  const currentId = data?.currentSessionId ?? "";

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Active sessions
      </Typography>
      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No active sessions found.
        </Typography>
      ) : (
        <Table size="small" aria-label="Active sessions">
          <TableHead>
            <TableRow>
              <TableCell>Device</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Last seen</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((s) => {
              const isCurrent = s.sessionId === currentId || s.current;
              return (
                <TableRow key={s.sessionId}>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>{s.device}</span>
                      {isCurrent ? (
                        <Chip
                          label="This device"
                          size="small"
                          color="primary"
                        />
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell>{s.country}</TableCell>
                  <TableCell>{s.lastSeenAt}</TableCell>
                  <TableCell align="right">
                    {!isCurrent ? (
                      <Button
                        size="small"
                        onClick={() => {
                          terminate.mutate(s.sessionId, {
                            onSuccess: () => {
                              toast("Session terminated");
                            },
                          });
                        }}
                      >
                        Sign out
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button
          variant="outlined"
          color="error"
          onClick={() => {
            setConfirmAll(true);
          }}
          disabled={items.filter((s) => !s.current).length === 0}
        >
          Sign out from all other devices
        </Button>
      </Stack>
      <Collapse in={confirmAll}>
        <Alert
          severity="warning"
          sx={{ mt: 2 }}
          action={
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                onClick={() => {
                  setConfirmAll(false);
                }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                color="error"
                onClick={() => {
                  terminateOthers.mutate(undefined, {
                    onSuccess: () => {
                      toast("Signed out from other devices");
                      setConfirmAll(false);
                    },
                    onError: () => {
                      toast("Couldn't sign out other devices", "error");
                    },
                  });
                }}
              >
                Confirm
              </Button>
            </Stack>
          }
        >
          Sign out from all other devices?
        </Alert>
      </Collapse>
    </Box>
  );
}

function MfaTable(): ReactElement {
  const { data } = useMfaFactors();
  const enroll = useEnrollMfa();
  const { toast } = useToast();
  const items = data?.items ?? [];

  function enrolType(type: "totp" | "webauthn" | "sms") {
    enroll.mutate(
      { type },
      {
        onSuccess: (res) => {
          if (typeof window !== "undefined") {
            window.open(res.enrollmentUrl, "_blank", "noopener,noreferrer");
          }
        },
        onError: () => {
          toast("Couldn't start enrolment", "error");
        },
      },
    );
  }

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Two-factor authentication
      </Typography>
      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No second factor enrolled.
        </Typography>
      ) : (
        <Table size="small" aria-label="MFA factors">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Label</TableCell>
              <TableCell>Enrolled</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((f) => (
              <TableRow key={f.id}>
                <TableCell>{f.type.toUpperCase()}</TableCell>
                <TableCell>{f.label}</TableCell>
                <TableCell>{f.enrolledAt ?? "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {/* SB6-AUTH-2FA-01 — friendly labels + SMS hidden.
          Christian [00:15:34]: TOTP / WebAuthn / SMS are not
          self-explanatory for non-techies. He also pointed out that
          rendering "Enrol SMS" when no SMS provider is wired produces
          a dead-end on click. Until we expose a Zitadel-policy probe
          (likely GET /api/auth/v1/2fa/methods returning the
          org-configured factors), we hide SMS unconditionally — the
          local dev stack has no SMS provider and no production tenant
          currently uses SMS. Re-enable as a feature flag once a
          provider is configured. */}
      <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
        <Button
          variant="outlined"
          onClick={() => {
            enrolType("totp");
          }}
        >
          Authenticator app (any TOTP-compatible)
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            enrolType("webauthn");
          }}
        >
          Security key or biometric (YubiKey · Windows Hello · Touch ID)
        </Button>
      </Stack>
    </Box>
  );
}
